import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './OurStory.css';

const COLORS = {
  dark: '#22223b',
  grey: '#4a4e69',
  purple: '#9a8c98',
  pink: '#c9ada7',
  beige: '#f2e9e4',
};

const navStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  background: '#ffffff',
  borderBottom: '1px solid #e0e0e0',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
  padding: '1rem 2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '70px',
};

const brandStyle = {
  fontFamily: 'Playfair Display, serif',
  fontSize: '1.8rem',
  fontWeight: 700,
  color: '#9a8c98',
  textDecoration: 'none',
  letterSpacing: '0.02em',
  transition: 'color 0.2s ease',
};

const navLinkStyle = {
  color: COLORS.grey,
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: '1.1rem',
  marginLeft: '2rem',
  letterSpacing: '0.01em',
  transition: 'color 0.2s',
};

const mainStyle = {
  background: '#ffffff',
  minHeight: '100vh',
  padding: '0',
  fontFamily: '"Montserrat", sans-serif',
};

const blogCardStyle = {
  maxWidth: '720px',
  margin: '3.5rem auto 2.5rem auto',
  background: '#ffffff',
  padding: '3.5rem 2.5rem 2.5rem 2.5rem',
  color: COLORS.dark,
  lineHeight: 1.8,
  fontSize: '1.13rem',
  position: 'relative',
};

const accentBarStyle = {
  width: '70px',
  height: '6px',
  background: COLORS.pink,
  borderRadius: '3px',
  margin: '0 0 2.5rem 0',
};

const blogTitleStyle = {
  color: COLORS.grey,
  fontFamily: '"Montserrat", sans-serif',
  fontWeight: 800,
  fontSize: '2.5rem',
  marginBottom: '0.5rem',
  letterSpacing: '0.01em',
  lineHeight: 1.2,
};

const blogSubtitleStyle = {
  color: COLORS.purple,
  fontFamily: '"Montserrat", sans-serif',
  fontWeight: 600,
  fontSize: '1.25rem',
  marginBottom: '2.2rem',
  letterSpacing: '0.01em',
};

const sectionTitleStyle = {
  color: COLORS.purple,
  fontFamily: '"Montserrat", sans-serif',
  fontWeight: 700,
  fontSize: '1.5rem',
  margin: '2.5rem 0 1.2rem 0',
  letterSpacing: '0.01em',
};

const dividerStyle = {
  border: 'none',
  borderTop: `1.5px solid ${COLORS.beige}`,
  margin: '2.5rem 0',
};

const highlightStyle = {
  color: COLORS.purple,
  fontWeight: 600,
};

const OurStory = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="homepage" style={mainStyle}>
      <nav style={navStyle}>
        <Link to="/" style={brandStyle}>Elysian</Link>
      </nav>
      <article style={blogCardStyle}>
        <h1 style={blogTitleStyle}>Our Story</h1>
        <div style={blogSubtitleStyle}>About Us: A Note From Our Founder</div>
        <p style={{ fontWeight: 600, color: COLORS.dark, fontSize: '1.15rem', textAlign: 'left' }}>
          I never planned to launch a diamond business—I simply wanted to find the perfect engagement ring.
        </p>
        <p style={{ textAlign: 'left' }}>
          Like many, I discovered that choosing a ring felt overwhelming. Endless options in cuts, certifications, settings, and unclear pricing left me confused. Store visits often added pressure, with salespeople pushing expensive choices rather than listening to what truly mattered to me.
        </p>
        <p style={{ textAlign: 'left' }}>
          When I finally found a ring I loved, I asked: Why should something symbolizing love feel so needlessly complex?
        </p>
        <p style={{ ...highlightStyle, textAlign: 'left' }}>That's why I created Elysian Stone Design.</p>
        <hr style={dividerStyle} />
        <h2 style={sectionTitleStyle}>Our Mission</h2>
        <p style={{ textAlign: 'left' }}>
          We believe ring shopping should be joyful, simple, and free of pressure—a celebration, not a chore.
        </p>
        <h2 style={sectionTitleStyle}>Your Personal Concierge</h2>
        <p style={{ textAlign: 'left' }}>
          We understand how overwhelming ring selection can be. That's why we act as your dedicated guide, streamlining your journey to the perfect match so you can focus on life's meaningful moments.
        </p>
        <h2 style={sectionTitleStyle}>Uncompromising Quality, Backed by Experts</h2>
        <p style={{ textAlign: 'left' }}>
          While we simplify your experience, we never compromise on quality. Every ring is:
        </p>
        <ul style={{ marginLeft: '1.5em', marginBottom: '1.5rem', textAlign: 'left' }}>
          <li>Crafted by <strong>master artisans with 20+ years of heritage</strong></li>
          <li>Rigorously inspected by <strong>independent gemologists</strong></li>
          <li>Created using sustainable practices verified by <strong>SCS Global Standards</strong></li>
        </ul>
        <p style={{ textAlign: 'left' }}>
          Your ring isn't just beautiful—it's built to last generations with ethical integrity.
        </p>
        <h2 style={sectionTitleStyle}>The Integrity of IGI-Certified Lab-Grown Diamonds</h2>
        <p style={{ textAlign: 'left' }}>
          Every stone we offer is <strong>IGI-certified lab-grown</strong>, combining ethical excellence with scientific precision:
        </p>
        <ul style={{ marginLeft: '1.5em', marginBottom: '1.5rem', textAlign: 'left' }}>
          <li><strong>Guaranteed Authenticity</strong>: IGI (International Gemological Institute) provides the world's most trusted certification, verifying every diamond's cut, color, clarity, and carat weight.</li>
          <li><strong>Sustainable Innovation</strong>: When produced with renewable energy (as in our partner facilities), lab-grown diamonds reduce carbon emissions by up to 90% compared to mined diamonds.</li>
          <li><strong>Exceptional Value</strong>: Physically and chemically identical to mined diamonds, yet typically <strong>30-40% more affordable</strong> for matched quality—making extraordinary beauty accessible.</li>
          <li><strong>Future-Proof Assurance</strong>: Each diamond features IGI's laser inscription for lifelong traceability.</li>
        </ul>
        <h2 style={sectionTitleStyle}>Designed for You</h2>
        <p style={{ textAlign: 'left' }}>Our platform empowers your journey:</p>
        <ul style={{ marginLeft: '1.5em', marginBottom: '1.5rem', textAlign: 'left' }}>
          <li><strong>Thoughtful yet efficient</strong>: Skip months of research—our experts do the heavy lifting.</li>
          <li><strong>Beautiful without the hard sell</strong>: Prioritize meaning, not upsells.</li>
          <li><strong>Ethical & transparent</strong>: Know your ring's origin with certified sustainability.</li>
        </ul>
        <h2 style={sectionTitleStyle}>Your Journey, Simplified</h2>
        <ul style={{ marginLeft: '1.5em', marginBottom: '1.5rem', textAlign: 'left' }}>
          <li><strong>Take our short quiz</strong>: Find your perfect match in minutes, not months.</li>
          <li><strong>Explore curated collections</strong>: Every design handcrafted by seasoned artisans.</li>
          <li><strong>Create with confidence</strong>: Your personal concierge + industry veterans = zero guesswork.</li>
        </ul>
        <p style={{ textAlign: 'left' }}>
          You don't need diamond expertise—that's our role. We're here to ensure you feel <strong>empowered, excited, and assured</strong> about your choice.
        </p>
        <p style={{ textAlign: 'left' }}>
          Ready to propose or just exploring? We make it effortless, authentic, and genuinely uplifting.
        </p>
        <p style={{ textAlign: 'left' }}>
          Welcome to Elysian Stone Design: Where <strong>master craftsmanship</strong>, <strong>scientifically advanced diamonds</strong>, and <strong>human-centered service</strong> create symbols of love you can trust.
        </p>
      </article>
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section about">
            <h4>About Us</h4>
            <p>
              <strong>Make buying a ring feel as good as the moment it represents.</strong>
            </p>
            <p style={{textAlign: 'center'}}>
              No pressure. No guesswork. Just clarity, joy, and beautiful rings.<br/>
            </p>
            <p style={{marginBottom:'10px', textAlign: 'center'}}>
              <span style={{ display: 'block', marginTop: '1em' }}>
                <Link to="/our-story" style={{ color: '#f2e9e4', textDecoration: 'underline' }}>Read our full story</Link>
              </span>
            </p>
          </div>
          <div className="footer-section contact">
            <h4>Contact Us</h4>
            <p>Email: contact@elysianstonedesign.com</p>
          </div>
          <div className="footer-section others">
            <h4>Others</h4>
            <ul>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="footer-copyright">
          &copy; {new Date().getFullYear()} Elysian Stone Design. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default OurStory; 